import { SegmentedControl, Stack, Text, Tooltip } from '@mantine/core';
import React, { useMemo, useState } from 'react';

import {
  DeviceType,
  IncidentClosedMethodEnum,
  INCIDENTS_API_URL,
  incidentsQueryKeys,
  IncidentType,
  useIncidents,
} from '@portals/api/organizations';
import { TextWithTooltip } from '@portals/core';
import { IncidentStatusBadge } from '@portals/framework';
import { PaginatedTable } from '@portals/table';
import {
  PaginatedFilterTypeEnum,
  TableColumn,
  TableFilterTypeEnum,
} from '@portals/types';
import {
  formatDateTime,
  formatDuration,
  PRIORITY_OPTIONS,
  timeFromNow,
} from '@portals/utils';

import { IncidentDetailsPanel } from '../../../../components/Incidents/IncidentDetailsPanel';
import { useAssigneeSelect } from '../../../../pages/incidents/assignee-select/assignee-select.hooks';
import { AssigneeSelect } from '../../../../pages/incidents/assignee-select/AssigneeSelect';
import { PriorityColumn } from '../../../../pages/incidents/PriorityColumn';

interface IncidentsProps {
  device: DeviceType;
}

const FILTER_OPTIONS = {
  active: 'Active',
  snoozed: 'Snoozed',
  resolved: 'Resolved',
} as const;

type FilterValues = (typeof FILTER_OPTIONS)[keyof typeof FILTER_OPTIONS];

export const getDefaultFilters = (
  deviceId: string,
  selectedTab: FilterValues
): string => {
  switch (selectedTab) {
    case FILTER_OPTIONS.active:
      return `?q[device_id_eq]=${deviceId}&[status_filter]=active`;

    case FILTER_OPTIONS.snoozed:
      return `?q[device_id_eq]=${deviceId}&[status_filter]=snoozed`;

    case FILTER_OPTIONS.resolved:
    default:
      return `?q[device_id_eq]=${deviceId}&[status_filter]=closed`;
  }
};

export function Incidents({ device }: IncidentsProps) {
  const [filter, setFilter] = useState<FilterValues>(FILTER_OPTIONS.active);
  const { assignableUsers, assigneeOptions } = useAssigneeSelect();

  const columns = useMemo(() => {
    const columns: TableColumn<IncidentType>[] = [
      {
        dataField: 'title',
        text: 'Title',
        sort: true,
        isSticky: true,
        filter: {
          type: TableFilterTypeEnum.Text,
        },
        formatter: (_, { title, description }) => (
          <TextWithTooltip label={title}>
            <Text c="gray.7" mb={4} truncate>
              {title}
            </Text>
            <Text c="gray.5" truncate>
              {description}
            </Text>
          </TextWithTooltip>
        ),
      },
      {
        dataField: 'priority',
        text: 'Priority',
        maxWidth: 170,
        minWidth: 170,
        sort: true,
        filter: {
          type: PaginatedFilterTypeEnum.Select,
          options: PRIORITY_OPTIONS,
        },
        formatter: (_, incident) => <PriorityColumn incident={incident} />,
      },
      {
        dataField: 'short_id',
        text: 'ID',
      },
      {
        dataField: 'created_at',
        text: 'Created',
        sort: true,
        filter: {
          type: TableFilterTypeEnum.Date,
        },
        formatter: (_, { created_at }) => (
          <Tooltip
            label={formatDateTime(created_at)}
            position="top-start"
            withArrow
          >
            <Text>{timeFromNow(created_at)}</Text>
          </Tooltip>
        ),
      },
      {
        dataField: 'assignee',
        text: 'Assigned to',
        minWidth: 220,
        formatter: (_, incident) => (
          <AssigneeSelect
            assignableUsers={assignableUsers}
            assigneeOptions={assigneeOptions}
            incident={incident}
            data-analyticsid="device-modal-incidents-table-row-select-assignee"
          />
        ),
      },
      {
        dataField: 'status',
        text: 'Status',
        formatter: (_, incident) => <IncidentStatusBadge incident={incident} />,
      },
      {
        dataField: 'updated_at',
        text: 'Updated',
        hidden: true,
        sort: true,
        filter: {
          type: TableFilterTypeEnum.Date,
        },
        formatter: (_, { updated_at }) => {
          if (!updated_at) return null;

          return (
            <Tooltip
              label={formatDateTime(updated_at)}
              position="top-start"
              withArrow
            >
              <Text>{timeFromNow(updated_at)}</Text>
            </Tooltip>
          );
        },
      },
    ];

    if (filter === FILTER_OPTIONS.resolved) {
      columns.push({
        dataField: 'closed_method',
        text: 'Resolved by',
        sort: true,
        minWidth: 215,
        filter: {
          type: TableFilterTypeEnum.Select,
          options: {
            [IncidentClosedMethodEnum.Manually]: 'Manually',
            [IncidentClosedMethodEnum.Automatically]: 'Automatically',
            [IncidentClosedMethodEnum.Device]: 'Device',
            [IncidentClosedMethodEnum.Integration]: 'Integration',
          },
        },
        formatter: (_, { closed_method, closed_by }) => {
          if (!closed_method) return null;

          return (
            <Text truncate>
              <Text span color="gray.7" tt="capitalize">
                {closed_method}{' '}
              </Text>
              {closed_by ? (
                <Text span color="gray.5" title={closed_by.name}>
                  (by {closed_by.name})
                </Text>
              ) : null}
            </Text>
          );
        },
      });

      columns.push({
        dataField: 'duration',
        text: 'Duration',
        minWidth: 215,
        formatter: (_, { closed_at, created_at }) => {
          if (!closed_at) return null;

          return formatDuration({
            startDate: created_at,
            endDate: closed_at,
          });
        },
      });
    }

    return columns;
  }, [assignableUsers, assigneeOptions, filter]);

  return (
    <Stack
      spacing="xl"
      py="xl"
      sx={{
        height: '100%',
      }}
    >
      <SegmentedControl
        data={[
          FILTER_OPTIONS.active,
          FILTER_OPTIONS.snoozed,
          FILTER_OPTIONS.resolved,
        ]}
        onChange={(filter: FilterValues) => setFilter(filter)}
        value={filter}
        data-testid="location-tabs"
      />

      <PaginatedTable<IncidentType>
        key={filter}
        keyField="id"
        name={`device-incidents-${filter}`}
        columns={columns}
        dataHookUrl={`${INCIDENTS_API_URL}/${getDefaultFilters(
          device.id,
          filter
        )}`}
        dataHook={useIncidents}
        dataHookQueryKey={[...incidentsQueryKeys.base, 'device-details']}
        noDataIndication={{
          title:
            filter === FILTER_OPTIONS.resolved
              ? 'No incidents'
              : 'No resolved incidents',
        }}
        noHeader
        isUrlSyncEnabled={false}
        defaultSortBy={
          filter === FILTER_OPTIONS.resolved
            ? [{ id: 'created_at', desc: true }]
            : [{ id: 'priority', desc: false }]
        }
        detailsPanel={{
          type: 'inner',
          renderer: ({ row, onClose }) => (
            <IncidentDetailsPanel
              incident={row.original}
              onClosePanel={onClose}
            />
          ),
        }}
      />
    </Stack>
  );
}
